// ============================================================================
// Admin Page — Operator Runtime Controls (S1-004)
// ============================================================================
// Toggle demo mode + budget multiplier tanpa code deploy. Settings tersimpan
// di Supabase `osint_runtime_config` (key/value), backend api/osint-stream.js
// baca sekali per request → kalau ON, PLATFORM_BUDGETS_MS dikalikan multiplier.
//
// Akses: page='admin' di sessionStorage atau URL ?page=admin (lihat App.jsx).
// SecurityGate level="admin" sudah handle auth gating sebelum render.
// ============================================================================
import { useState, useEffect } from 'react';
import { Settings, AlertTriangle, Check, Loader2 } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { supabase } from '../lib/supabase';

const Admin = () => {
  const { lang } = useApp();
  const [demoMode, setDemoMode] = useState(false);
  const [multiplier, setMultiplier] = useState(2);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { loadConfig(); }, []);

  async function loadConfig() {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('osint_runtime_config')
        .select('key, value');
      if (err) throw err;
      if (!data) {
        setError(lang === 'id'
          ? 'Tabel osint_runtime_config belum ada. Run migration di SQL Editor.'
          : 'Table osint_runtime_config not found. Run migration in SQL Editor.');
        return;
      }
      const enabled = data.find(r => r.key === 'demo_mode_enabled')?.value === 'true';
      const mult = parseFloat(data.find(r => r.key === 'demo_mode_multiplier')?.value || '2');
      setDemoMode(enabled);
      setMultiplier(Number.isFinite(mult) ? Math.min(Math.max(mult, 1), 5) : 2);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function saveConfig(nextEnabled, nextMultiplier) {
    setSaving(true);
    setError(null);
    try {
      const updatedAt = new Date().toISOString();
      const rows = [
        { key: 'demo_mode_enabled', value: String(nextEnabled), updated_at: updatedAt },
        { key: 'demo_mode_multiplier', value: String(nextMultiplier), updated_at: updatedAt },
      ];
      const { data, error: err } = await supabase
        .from('osint_runtime_config')
        .upsert(rows, { onConflict: 'key' })
        .select();
      if (err) throw err;
      if (!data || data.length === 0) {
        throw new Error(lang === 'id'
          ? 'Update tidak persist (RLS blokir?). Cek policy osint_runtime_config_anon_all.'
          : 'Update did not persist (RLS blocked?). Check policy osint_runtime_config_anon_all.');
      }
      setSavedAt(Date.now());
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  const handleToggle = () => {
    const next = !demoMode;
    setDemoMode(next);
    saveConfig(next, multiplier);
  };

  const handleMultiplierChange = (val) => {
    const num = Math.min(Math.max(parseFloat(val) || 1, 1), 5);
    setMultiplier(num);
    if (demoMode) saveConfig(demoMode, num); // kalau ON, update langsung
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-[10px] font-mono" style={{ color: 'var(--text-mute)' }}>
        <Loader2 size={12} className="animate-spin" /> {lang === 'id' ? 'Memuat config...' : 'Loading config...'}
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto flex flex-col gap-4">
      <div className="flex items-center gap-3 pb-3" style={{ borderBottom: '1px solid var(--border-dim)' }}>
        <Settings size={18} color="var(--color-cyan)" />
        <div>
          <h1 className="text-[14px] font-black uppercase tracking-widest" style={{ color: 'var(--text-hi)' }}>
            {lang === 'id' ? 'Kendali Runtime' : 'Runtime Controls'}
          </h1>
          <p className="text-[9px] font-mono" style={{ color: 'var(--text-mute)' }}>
            {lang === 'id'
              ? 'Toggle config tanpa deploy ulang · efektif di request berikutnya'
              : 'Toggle config without redeploy · effective on next request'}
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-xl p-3 flex items-start gap-2" style={{
          background: 'rgba(255,45,85,0.08)', border: '1px solid rgba(255,45,85,0.4)',
        }}>
          <AlertTriangle size={14} color="#ff2d55" className="shrink-0 mt-0.5" />
          <div>
            <p className="text-[10px] font-bold" style={{ color: '#ff2d55' }}>
              {lang === 'id' ? 'Gagal' : 'Error'}
            </p>
            <p className="text-[9px] font-mono mt-1" style={{ color: 'var(--text-dim)' }}>{error}</p>
          </div>
        </div>
      )}

      {/* Demo Mode Toggle */}
      <div className="rounded-xl p-4 flex flex-col gap-3" style={{
        background: 'var(--color-card)', border: `1px solid ${demoMode ? 'var(--color-yellow)' : 'var(--border-dim)'}`,
      }}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <p className="text-[11px] font-black uppercase tracking-widest" style={{ color: 'var(--text-hi)' }}>
              {lang === 'id' ? 'Mode Demo (Budget Diperbesar)' : 'Demo Mode (Extended Budget)'}
            </p>
            <p className="text-[9px] font-mono mt-1" style={{ color: 'var(--text-mute)' }}>
              {lang === 'id'
                ? 'Saat ON: timeout per platform di pipeline OSINT dikalikan multiplier. Default 2x.'
                : 'When ON: per-platform timeout in OSINT pipeline multiplied. Default 2x.'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleToggle}
            disabled={saving}
            className="relative inline-flex items-center px-1 transition-colors"
            style={{
              width: 48, height: 24, borderRadius: 999,
              background: demoMode ? 'var(--color-yellow)' : 'var(--color-surface)',
              border: `1px solid ${demoMode ? 'var(--color-yellow)' : 'var(--border-mid)'}`,
              cursor: saving ? 'wait' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
            title={demoMode ? 'Click to disable demo mode' : 'Click to enable demo mode'}
          >
            <span style={{
              width: 18, height: 18, borderRadius: '50%',
              background: demoMode ? 'var(--color-bg)' : 'var(--text-mute)',
              transform: demoMode ? 'translateX(22px)' : 'translateX(0)',
              transition: 'transform 200ms ease, background 200ms ease',
              display: 'block',
            }} />
          </button>
        </div>

        {demoMode && (
          <div className="rounded-lg p-2.5 flex items-start gap-2" style={{
            background: 'rgba(255,214,10,0.08)', border: '1px solid rgba(255,214,10,0.3)',
          }}>
            <AlertTriangle size={12} color="var(--color-yellow)" className="shrink-0 mt-0.5" />
            <p className="text-[9px] font-mono" style={{ color: 'var(--color-yellow)' }}>
              {lang === 'id'
                ? 'Demo mode AKTIF — biaya Apify per run lebih tinggi. Toggle OFF setelah demo selesai.'
                : 'Demo mode ACTIVE — higher Apify cost per run. Toggle OFF after demo.'}
            </p>
          </div>
        )}

        <div className="flex items-center gap-3">
          <label className="text-[9px] font-mono uppercase tracking-widest" style={{ color: 'var(--text-mute)' }}>
            {lang === 'id' ? 'Multiplier' : 'Multiplier'}
          </label>
          <input
            type="number"
            min="1"
            max="5"
            step="0.5"
            value={multiplier}
            onChange={(e) => setMultiplier(parseFloat(e.target.value) || 1)}
            onBlur={(e) => handleMultiplierChange(e.target.value)}
            disabled={saving}
            className="px-2 py-1 text-[10px] font-mono rounded"
            style={{
              width: 64, background: 'var(--color-surface)', color: 'var(--text-hi)',
              border: '1px solid var(--border-mid)',
            }}
          />
          <span className="text-[8px] font-mono" style={{ color: 'var(--text-mute)' }}>
            {lang === 'id' ? '× clamp 1.0–5.0 (safety ceiling)' : '× clamp 1.0–5.0 (safety ceiling)'}
          </span>
        </div>

        <div className="text-[8px] font-mono pt-2" style={{ color: 'var(--text-mute)', borderTop: '1px solid var(--border-dim)' }}>
          {lang === 'id' ? 'Status:' : 'Status:'}
          <span className="font-bold ml-1" style={{ color: demoMode ? 'var(--color-yellow)' : 'var(--color-green)' }}>
            {demoMode ? 'ON' : 'OFF'}
          </span>
          {demoMode && (
            <>
              <span className="ml-2">·</span>
              <span className="ml-2">
                {lang === 'id' ? 'Budget timeout' : 'Budget timeout'}:{' '}
                <span className="font-bold" style={{ color: 'var(--color-cyan)' }}>{multiplier}×</span>
              </span>
            </>
          )}
          {savedAt && (
            <>
              <span className="ml-2">·</span>
              <Check size={9} color="var(--color-green)" className="inline ml-1" />
              <span className="ml-1" style={{ color: 'var(--color-green)' }}>
                {lang === 'id' ? 'tersimpan' : 'saved'} {new Date(savedAt).toLocaleTimeString('id-ID', { hour12: false })}
              </span>
            </>
          )}
          {saving && (
            <>
              <span className="ml-2">·</span>
              <Loader2 size={9} className="animate-spin inline ml-1" />
              <span className="ml-1">{lang === 'id' ? 'menyimpan...' : 'saving...'}</span>
            </>
          )}
        </div>
      </div>

      {/* Reference info */}
      <div className="rounded-xl p-3 text-[9px] font-mono leading-relaxed" style={{
        background: 'var(--color-surface)', border: '1px solid var(--border-dim)', color: 'var(--text-dim)',
      }}>
        <p className="font-bold mb-1" style={{ color: 'var(--text-hi)' }}>
          {lang === 'id' ? 'Catatan' : 'Notes'}
        </p>
        <ul className="space-y-1 list-disc list-inside">
          <li>{lang === 'id'
            ? 'Multiplier diterapkan ke PLATFORM_BUDGETS_MS (timeout per platform Apify)'
            : 'Multiplier applied to PLATFORM_BUDGETS_MS (Apify per-platform timeout)'}</li>
          <li>{lang === 'id'
            ? 'Backend baca config sekali per request (cached di scope handler)'
            : 'Backend reads config once per request (cached in handler scope)'}</li>
          <li>{lang === 'id'
            ? 'Fail-safe: kalau Supabase down → fallback ke dev defaults (1×)'
            : 'Fail-safe: if Supabase down → fallback to dev defaults (1×)'}</li>
          <li>{lang === 'id'
            ? 'Default OFF supaya cost tetap aman di luar window demo'
            : 'Default OFF so cost stays safe outside demo windows'}</li>
        </ul>
      </div>
    </div>
  );
};

export default Admin;
